// frontend/src/components/admin/enquiry/EnquiriesComponent.tsx
import React, { useEffect, useState } from "react";
import styles from "./EnquiriesComponent.module.css";
import axiosInstance from "../../../configuration/api";

interface Enquiry {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  interest: string;
  status: string;
  createdAt: string;
  message: string;
  reply: string; // Changed from response to reply
  quantity?: string;
  company?: string;
  productId?: {
    _id: string;
    productId: string;
    name: string;
  };
  seriesId?: {
    _id: string;
    id: string;
    title: string;
  };
  pincode?: string;
  address?: string;
}

interface ApiResponse {
  status: string;
  results: number;
  total: number;
  page: number;
  totalPages: number;
  data: {
    enquiries: Enquiry[];
  };
}

const EnquiriesComponent = () => {
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingReply, setEditingReply] = useState<{
    // Changed from editingResponse
    id: string;
    reply: string; // Changed from response
  } | null>(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Enquiry | null>(null);

  // Fetch enquiries from the API
  const fetchEnquiries = async (pageNum: number = 1) => {
    try {
      setLoading(true);
      setError("");
      const baseUrl = process.env.REACT_APP_API_HOST;
      const response = await axiosInstance.get(
        `${baseUrl}/api/v1/enquiry?page=${pageNum}&limit=10`
      );
      const data: ApiResponse = response.data;

      if (response.status === 200) {
        setEnquiries((prev) =>
          pageNum === 1
            ? data.data.enquiries
            : [...prev, ...data.data.enquiries]
        );
        setTotalPages(data.totalPages);
        setPage(pageNum);
      } else {
        setError(data.status || "Failed to fetch enquiries");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Network error. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  // Load more enquiries
  const loadMore = () => {
    if (page < totalPages) {
      fetchEnquiries(page + 1);
    }
  };

  // Update enquiry status and response

  const updateEnquiry = async (id: string, status: string, reply: string) => {
    try {
      // Prevent status changes for new enquiries
      const enquiry = enquiries.find((e) => e._id === id);
      if (enquiry?.status === "new" && status !== "new") {
        setError("Cannot change status until you reply");
        return false;
      }

      const res = await axiosInstance.patch(`/api/v1/enquiry/${id}`, {
        status,
        reply,
      });

      if (res.status === 200) {
        setEnquiries((prev) =>
          prev.map((enquiry) =>
            enquiry._id === id ? { ...enquiry, status, reply } : enquiry
          )
        );
        return true;
      }
      return false;
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to update enquiry. Please try again."
      );
      return false;
    }
  };
  // View enquiry details
  const viewEnquiryDetails = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/api/v1/enquiry/${id}`);
      if (response.status === 200) {
        setSelectedEnquiry(response.data.data.enquiry);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Failed to fetch enquiry details. Please try again."
      );
    }
  };

  // Start editing reply
  const startEditingReply = (id: string, currentReply: string) => {
    // Changed from response
    setEditingReply({ id, reply: currentReply }); // Changed from response
  };

  // Save edited response
  const saveReply = async () => {
    if (editingReply) {
      try {
        setLoading(true);
        const res = await axiosInstance.patch(
          `/api/v1/enquiry/${editingReply.id}`,
          { reply: editingReply.reply } // Only send reply
        );

        if (res.status === 200) {
          setEnquiries((prev) =>
            prev.map((e) =>
              e._id === editingReply.id
                ? { ...e, reply: editingReply.reply, status: "contacted" }
                : e
            )
          );
          setEditingReply(null);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to save reply");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  return (
    <div className={styles.enquiriesContainer}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Customer Enquiries</h3>
        <button
          className={styles.refreshButton}
          onClick={() => fetchEnquiries(1)}
        >
          Refresh
        </button>
      </div>

      {loading && page === 1 ? (
        <div className={styles.loading}>Loading enquiries...</div>
      ) : error ? (
        <div className={styles.error}>
          {error}
          <button onClick={() => fetchEnquiries(1)}>Retry</button>
        </div>
      ) : enquiries.length === 0 ? (
        <div className={styles.noResults}>No enquiries found</div>
      ) : (
        <>
          <div className={styles.enquiryTableContainer}>
            <table className={styles.enquiryTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Product</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Reply</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((enquiry) => (
                  <tr key={enquiry._id}>
                    <td>{enquiry.fullName}</td>
                    <td>
                      <div>{enquiry.email}</div>
                      <div>{enquiry.phone}</div>
                    </td>
                    <td>
                      {enquiry.seriesId?.title} - {enquiry.productId?.name}
                    </td>
                    <td>{new Date(enquiry.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          enquiry.status === "new"
                            ? styles.statusNew
                            : enquiry.status === "contacted"
                            ? styles.statusContacted
                            : enquiry.status === "in_progress"
                            ? styles.statusInProgress
                            : enquiry.status === "resolved"
                            ? styles.statusResolved
                            : styles.statusClosed
                        }`}
                      >
                        {enquiry.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className={styles.replyCell}>
                      {editingReply?.id === enquiry._id ? (
                        <div className={styles.replyEdit}>
                          <textarea
                            value={editingReply.reply}
                            onChange={(e) =>
                              setEditingReply({
                                id: enquiry._id,
                                reply: e.target.value,
                              })
                            }
                            className={styles.replyInput}
                            rows={3}
                          />
                          <div className={styles.replyActions}>
                            <button
                              className={styles.saveButton}
                              onClick={saveReply}
                            >
                              Save
                            </button>
                            <button
                              className={styles.cancelButton}
                              onClick={() => setEditingReply(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : enquiry.status === "new" ? (
                        <button
                          className={styles.replyButton}
                          onClick={() =>
                            startEditingReply(enquiry._id, enquiry.reply)
                          }
                        >
                          Reply
                        </button>
                      ) : (
                        <div className={styles.replyDisplay}>
                          {enquiry.reply || "No reply yet"}
                          {enquiry.reply && (
                            <button
                              className={styles.editReplyButton}
                              onClick={() =>
                                startEditingReply(enquiry._id, enquiry.reply)
                              }
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.viewButton}
                          onClick={() => viewEnquiryDetails(enquiry._id)}
                        >
                          Details
                        </button>
                        <select
                          value={enquiry.status}
                          onChange={(e) =>
                            updateEnquiry(
                              enquiry._id,
                              e.target.value,
                              enquiry.reply
                            )
                          }
                          disabled={enquiry.status === "new"}
                          className={styles.statusSelect}
                        >
                          {enquiry.status === "new" ? (
                            <option value="new">New</option>
                          ) : (
                            <>
                              <option value="contacted">Contacted</option>
                              <option value="in_progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </>
                          )}
                        </select>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {page < totalPages && (
            <div className={styles.loadMoreContainer}>
              <button
                className={styles.loadMoreButton}
                onClick={loadMore}
                disabled={loading}
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Enquiry Detail Modal */}
      {selectedEnquiry && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Enquiry Details</h3>
              <button
                className={styles.closeButton}
                onClick={() => setSelectedEnquiry(null)}
              >
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <strong>Name:</strong> {selectedEnquiry.fullName}
              </div>
              <div className={styles.detailRow}>
                <strong>Email:</strong> {selectedEnquiry.email}
              </div>
              <div className={styles.detailRow}>
                <strong>Phone:</strong> {selectedEnquiry.phone}
              </div>
              <div className={styles.detailRow}>
                <strong>Company:</strong> {selectedEnquiry.company || "N/A"}
              </div>
              <div className={styles.detailRow}>
                <strong>Product:</strong> {selectedEnquiry.seriesId?.title} -{" "}
                {selectedEnquiry.productId?.name}
              </div>
              <div className={styles.detailRow}>
                <strong>Product ID:</strong>{" "}
                {selectedEnquiry.productId?.productId}
              </div>
              <div className={styles.detailRow}>
                <strong>Quantity:</strong>{" "}
                {selectedEnquiry.quantity || "Not specified"}
              </div>
              <div className={styles.detailRow}>
                <strong>PIN Code:</strong> {selectedEnquiry.pincode || "N/A"}
              </div>
              <div className={styles.detailRow}>
                <strong>Address:</strong> {selectedEnquiry.address || "N/A"}
              </div>
              <div className={styles.detailRow}>
                <strong>Status:</strong>{" "}
                <span
                  className={
                    styles[`status${selectedEnquiry.status.replace("_", "")}`]
                  }
                >
                  {selectedEnquiry.status.replace("_", " ")}
                </span>
              </div>
              <div className={styles.detailRow}>
                <strong>Created At:</strong>{" "}
                {new Date(selectedEnquiry.createdAt).toLocaleString()}
              </div>
              <div className={styles.detailSection}>
                <h4>Message:</h4>
                <p>{selectedEnquiry.message}</p>
              </div>
              {selectedEnquiry.reply && (
                <div className={styles.detailSection}>
                  <h4>Admin Response:</h4>
                  <p>{selectedEnquiry.reply}</p>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.closeModalButton}
                onClick={() => setSelectedEnquiry(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnquiriesComponent;
